<template>
  <v-app>
    <!--
      The drawer is a desktop thing. Below 960 it is not rendered at all and
      the three destinations live in the bar at the foot of the screen, where a
      thumb already is.
    -->
    <vertical-nav-menu
      v-if="wide"
      :is-drawer-open.sync="isDrawerOpen"
    ></vertical-nav-menu>

    <!--
      A bar that says where you are. It used to be transparent and hold nothing
      but a language switcher, a theme switcher and an avatar - so on a
      telephone there was no way to tell one screen from another except by what
      had loaded into it.
    -->
    <!-- no colour given on purpose: Vuetify's own sheet background follows the
         light and dark themes, and a named colour would not -->
    <v-app-bar app flat class="parent-bar">
      <div class="boxed-container w-full d-flex align-center px-2 px-sm-4">
        <v-btn
          v-if="backTo"
          icon
          class="me-1"
          :aria-label="$t('back')"
          @click="goBack"
        >
          <v-icon>{{ $vuetify.rtl ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
        </v-btn>

        <div class="text-h6 font-weight-medium text-truncate" style="min-width: 0">
          {{ title }}
        </div>

        <v-spacer></v-spacer>

        <lang-switcher></lang-switcher>
        <theme-switcher></theme-switcher>
        <app-bar-user-menu
          :admin-name="adminProfileStore.name"
          :admin-avatar="adminProfileStore.avatar"
          :profile-url="null"
          :admin="false"
        ></app-bar-user-menu>
      </div>
    </v-app-bar>

    <v-main>
      <!-- narrower gutters on a telephone: six is a lot of the screen -->
      <div class="app-content-container boxed-container pa-4 pa-sm-6">
        <slot></slot>
      </div>
    </v-main>

    <!--
      Three destinations, which is few enough that hiding them behind a
      hamburger costs a tap and buys nothing.
    -->
    <v-bottom-navigation
      v-if="!wide"
      app
      grow
      color="primary"
      class="parent-tabs"
    >
      <v-btn
        v-for="stop in destinations"
        :key="stop.name"
        :to="{ name: stop.name }"
        :aria-label="$t(stop.label)"
        text
      >
        <span class="text-caption">{{ $t(stop.label) }}</span>
        <v-icon>{{ stop.icon }}</v-icon>
      </v-btn>
    </v-bottom-navigation>
  </v-app>
</template>

<script>
/*
 * The chrome around the family's panel.
 *
 * It was copied from the school's, badges and all: a notification bell polling
 * an endpoint behind the school middleware every minute - which answers a
 * family 403 - to count unresolved complaints and drivers awaiting review, and
 * links to /drivers, /students and /complaints, none of which a parent may
 * open. The only thing in that reply a family had any use for was their own
 * name, and losing the request meant the account menu showed nobody's.
 *
 * So it asks the family's own endpoint instead, once, for the one thing it
 * needs.
 *
 * What was left after that was still an admin console: a 260px sidebar with
 * the product's logo in it, three links, and a transparent bar carrying no
 * title. On a telephone at twenty to eight that is a hamburger between a
 * parent and the only question they have.
 */
import VerticalNavMenu from './components/vertical-nav-menu/ParentVerticalNavMenu.vue'
import ThemeSwitcher from './components/ThemeSwitcher.vue'
import AppBarUserMenu from './components/AppBarUserMenu.vue'
import LangSwitcher from './components/LangSwitcher.vue'
import { adminProfileStore } from "@/utils/helpers"
import { portalChrome } from "@/utils/portalChrome"

/** The whole of the family's panel, in the order they are needed. */
const DESTINATIONS = [
  { name: 'my-children', label: 'my_children', icon: 'mdi-account-child-outline' },
  { name: 'money', label: 'payments', icon: 'mdi-wallet-outline' },
  { name: 'school-contact', label: 'contact_the_school', icon: 'mdi-school-outline' },
]

export default {
  components: {
    VerticalNavMenu,
    LangSwitcher,
    ThemeSwitcher,
    AppBarUserMenu,
  },
  setup() {
    return { adminProfileStore, portalChrome }
  },
  data() {
    return {
      isDrawerOpen: true,
      destinations: DESTINATIONS,
    }
  },
  computed: {
    /*
     * Wide enough for the drawer to stand open permanently. The same number is
     * given to the drawer itself as its mobile-breakpoint, so there is no band
     * of widths where it collapses into a hamburger nobody drew.
     */
    wide() {
      return !this.$vuetify.breakpoint.smAndDown
    },
    /*
     * A screen's name comes from its route, except the child's, which is the
     * child's own name and is not known until they have been fetched.
     */
    title() {
      if (portalChrome.title) return portalChrome.title

      const named = this.$route.meta && this.$route.meta.title

      return named ? this.$t(named) : ''
    },
    backTo() {
      return (this.$route.meta && this.$route.meta.backTo) || null
    },
  },
  mounted() {
    this.loadProfile()
  },
  methods: {
    /*
     * Back to where this screen hangs from, rather than back through history:
     * a family who arrived on a child's page from an emailed link has no
     * history to go back through, and $router.back() would leave the panel.
     */
    goBack() {
      this.$router.push({ name: this.backTo }).catch(() => {})
    },
    loadProfile() {
      axios
        .get("/parent-portal/children")
        .then(response => {
          const me = response.data.me
          if (!me) return

          adminProfileStore.name = me.name
          adminProfileStore.avatar = me.avatar
          adminProfileStore.id = me.id
        })
        //the chrome is not the place to report a failed request
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.v-app-bar ::v-deep {
  .v-toolbar__content {
    padding: 0;
  }
}

//a hairline rather than a shadow: the bar is a label, not a floating object
.parent-bar {
  border-bottom: 1px solid rgba(94, 86, 105, 0.14);
}

.parent-tabs ::v-deep .v-btn {
  //room for the word under the icon, which is what makes the bar readable to
  //somebody who does not already know what the icons mean
  min-width: 0;
}

.boxed-container {
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
}
</style>
